<template>
  <alzata-dialog
    ref="modal"
    size="lg"
    :title="dog?.id ? 'Edit Dog' : 'Create Dog'"
    seamless
  >
    <div class="row mt-n4">
      <div class="col-12">
        <alzata-tabs toEdge ref="tabContainer">
          <alzata-tab title="Summary Info">
            <div class="col-12">
              <label for="name" class="small">Name *</label>
              <input
                id="name"
                type="text"
                class="form-control"
                :class="{ 'is-invalid': errors.name }"
                placeholder="Name"
                v-model="name"
              />
              <div v-if="errors.name" class="invalid-feedback">
                Name is required.
              </div>
            </div>
            <div class="col-12">
              <label for="gender" class="small">Gender</label>
              <input
                id="gender"
                type="text"
                class="form-control"
                placeholder="Gender"
                v-model="gender"
              />
            </div>
            <div class="col-12">
              <label for="breed" class="small">Breed</label>
              <input
                id="breed"
                type="text"
                class="form-control"
                placeholder="Breed"
                v-model="breed"
              />
            </div>
            <div class="col-12">
              <label for="dateOfBirth" class="small">DOB *</label>
              <input
                id="dateOfBirth"
                type="text"
                class="form-control"
                :class="{ 'is-invalid': errors.dateOfBirth }"
                placeholder="Date of Birth (MM/DD/YYYY)"
                v-model="dateOfBirth"
              />
              <div v-if="errors.dateOfBirth" class="invalid-feedback">
                Date of birth is required in the format (MM/DD/YYYY).
              </div>
            </div>
            <div class="col-12">
              <label for="adoptionDate" class="small">Adoption Date</label>
              <input
                id="adoptionDate"
                type="text"
                class="form-control"
                :class="{ 'is-invalid': errors.adoptionDate }"
                placeholder="Adoption Date (MM/DD/YYYY)"
                v-model="adoptionDate"
              />
              <div v-if="errors.adoptionDate" class="invalid-feedback">
                Adoption Date must be in the format (MM/DD/YYYY).
              </div>
            </div>
            <div class="col-12">
              <label for="feedingInstructions" class="small">Feeding Instructions</label>
              <textarea
                rows="2"
                id="feedingInstructions"
                type="text"
                class="form-control"
                placeholder="Feeding Instructions"
                v-model="feedingInstructions"
              ></textarea>
            </div>
            <div class="col-12">
              <label for="veterinarian" class="small">Veterinarian</label>
              <select class="form-select" v-model="selectedVeterinarian">
                <option value="">Select a veterinarian</option>
                <option
                  v-for="veterinarian in veterinarians"
                  :value="veterinarian.id"
                >
                  {{ veterinarian.name }}
                </option>
              </select>
            </div>
            <div class="col-12">
              <label for="veterinarian" class="small">Emergency Veterinarian</label>
              <select
                class="form-select"
                v-model="selectedEmergencyVeterinarian"
              >
                <option value="">Select an emergency veterinarian</option>
                <option
                  v-for="veterinarian in emergencyVeterinarians"
                  :value="veterinarian.id"
                >
                  {{ veterinarian.name }}
                </option>
              </select>
            </div>
          </alzata-tab>
        </alzata-tabs>
      </div>
    </div>
    <template v-slot:modal-footer>
      <button type="button" class="btn btn-link" @click="hideCreateDog">
        Cancel
      </button>
      <button type="button" class="btn btn-primary" @click="save">Save</button>
    </template>
  </alzata-dialog>
</template>

<script>
import { getJson, postJson } from "../apis/baseApiClient";
import { DateTime } from "luxon";

export default {
  name: "CreateDogDialog",
  emits: ["save"],
  components: {
  },
  mounted() {
    (async () => {
      const veterinarians = await getJson("/api/v1/veterinarians?$orderby=Name%20desc");
      this.veterinarians = veterinarians;
      const emergencyVeterinarians = await getJson(
        "/api/v1/veterinarians?$orderby=Name%20desc&$filter=IsEmergency%20eq%20true"
      );
      this.emergencyVeterinarians = emergencyVeterinarians;
    })();
  },
  data() {
    return {
      dog: null,
      dogId: null,
      name: null,
      feedingInstructions: null,
      veterinarians: [],
      emergencyVeterinarians: [],
      selectedVeterinarian: null,
      selectedEmergencyVeterinarian: null,
      gender: null,
      breed: null,
      dateOfBirth: null,
      adoptionDate: null,
      errors: {
        name: false,
        dateOfBirth: false,
        adoptionDate: false,
      }
    };
  },
  methods: {
    clearValidation() {
      this.errors.name = false;
      this.errors.dateOfBirth = false;
      this.errors.adoptionDate = false;
    },
    show(dog) {
      (async () => {

        this.dog = dog;
        this.dogId = dog.id;
        this.name = dog.name;
        this.feedingInstructions = dog.feedingInstructions;
        this.selectedVeterinarian = dog.primaryVeterinarianId;
        this.selectedEmergencyVeterinarian = dog.emergencyVeterinarianId;
        this.gender = dog.gender;
        this.breed = dog.breed;
        this.dateOfBirth = dog.dateOfBirth ? this.formatDate(dog.dateOfBirth) : "";
        this.adoptionDate = dog.adoptionDate ? this.formatDate(dog.adoptionDate) : "";

        this.clearValidation();
        
        this.$refs.modal.show();
      })();
    },
    hideCreateDog() {
      this.dog = null;
      this.clearValidation();
      this.$refs.modal.hide();
    },
    formatDate(dateString) {
      const date = DateTime.fromISO(dateString);
      return date.toLocaleString(DateTime.DATE_SHORT);
    },
    formatDateRelative(dateString) {
      const date = DateTime.fromISO(dateString);
      return date.toRelative();
    },
    save() {
      this.errors.name = !this.name;

      const dob = DateTime.fromFormat(this.dateOfBirth, "M/d/yyyy");
      const isDobValid = dob.isValid;
      this.errors.dateOfBirth = !isDobValid;
      const adopt = DateTime.fromFormat(this.adoptionDate, "M/d/yyyy");
      const isAdoptValid = adopt.isValid;
        
      this.errors.adoptionDate = !isAdoptValid && this.adoptionDate.length;

      if (this.errors.name || this.errors.dateOfBirth || this.errors.adoptionDate) {
        return;
      }

      this.$emit("save", {
        id: this.dog?.id,
        name: this.name,
        feedingInstructions: this.feedingInstructions,
        primaryVeterinarianId: this.selectedVeterinarian,
        emergencyVeterinarianId: this.selectedEmergencyVeterinarian,
        gender: this.gender,
        breed: this.breed,
        dateOfBirth: this.dateOfBirth,
        adoptionDate: this.adoptionDate,
      });
    },
  },
};
</script>
